import {
  CalendarToday,
  FilterList,
  SocialDistance,
  LocationOn,
  ExpandMore,
} from "@mui/icons-material";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Button,
  InputAdornment,
  FormControl,
  Menu,
  Slider,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { observer } from "mobx-react-lite";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useEventPageContext } from "@root/stores/eventPageStore";
import { useAccountContext } from "@root/stores/accountStore";
import { Category } from "@root/utils/entities";
import moment from "moment";
import { DateRange, NumberRange } from "@root/utils/types";
import { EventSearchDefaults } from "@root/utils/enums";
import { CategorySelectDialog } from "@root/components/forms/steps/dialogbox/CategorySelectDialog";

// Shared rounded styling for the date selector text inputs (matches other filters)
const dateFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "25px",
    backgroundColor: "white",
    fontSize: { xs: "0.875rem", md: "1rem" },
    "& fieldset": { borderColor: "#e0e0e0" },
    "&:hover fieldset": { borderColor: "#FF6B35" },
    "&.Mui-focused fieldset": { borderColor: "#FF6B35" },
  },
};

const AGE_RANGES = [
  { label: "0-18", min: 0, max: 18 },
  { label: "18-30", min: 18, max: 30 },
  { label: "30-50", min: 30, max: 50 },
  { label: "50+", min: 50, max: 65 },
];

// NOTE: Date-range "Quick Select" preset dropdown was a client-requested change
// that has been POSTPONED (see requirements gathering). Per client, the core
// calendar date selector must stay. Keeping the preset code commented out (same
// as mobile event_filter_sheet.dart) so it can be re-enabled when approved.
// const DATE_RANGE_OPTIONS = [
//   { label: "This Week", value: "thisWeek" },
//   { label: "This Month", value: "thisMonth" },
//   { label: "Next Month", value: "nextMonth" },
//   { label: "Next 3 Months", value: "next3Months" },
// ];
//
// const getDateRangeFromOption = (option: string): DateRange | undefined => {
//   const today = moment().startOf("day");
//
//   switch (option) {
//     case "thisWeek":
//       return {
//         start: today.clone(),
//         end: today.clone().endOf("week"),
//       };
//     case "thisMonth":
//       return {
//         start: today.clone().startOf("month"),
//         end: today.clone().endOf("month"),
//       };
//     case "nextMonth":
//       return {
//         start: today.clone().add(1, "month").startOf("month"),
//         end: today.clone().add(1, "month").endOf("month"),
//       };
//     case "next3Months":
//       return {
//         start: today.clone(),
//         end: today.clone().add(3, "months"),
//       };
//     default:
//       return undefined;
//   }
// };

export const EventSearchFilters = observer(() => {
  const eventPageStore = useEventPageContext();
  const accountStore = useAccountContext();

  // Initialize state from store to preserve values across re-renders
  const [address, setAddress] = useState(eventPageStore.filters.address || "");
  const [mileRadius, setMileRadius] = useState<number>(
    eventPageStore.filters.mileRadius || EventSearchDefaults.MileRadius
  );
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    eventPageStore.filters.categories || []
  );
  const [selectedAge, setSelectedAge] = useState("");
  // Core calendar date selector — single "Date Range" field opening a popover with
  // Start/End DatePickers + Apply (restores previous origin/main behavior, see Map.tsx).
  const [startDate, setStartDate] = useState<moment.Moment | null>(null);
  const [endDate, setEndDate] = useState<moment.Moment | null>(null);
  // Popover anchor + draft values (committed to start/end only on Apply)
  const [dateMenuEl, setDateMenuEl] = useState<HTMLElement | null>(null);
  const [draftStart, setDraftStart] = useState<moment.Moment | null>(null);
  const [draftEnd, setDraftEnd] = useState<moment.Moment | null>(null);
  // const [selectedDateRange, setSelectedDateRange] = useState(""); // POSTPONED preset Quick Select
  // Radius popover anchor + draft value (committed to mileRadius only on Apply)
  const [radiusMenuEl, setRadiusMenuEl] = useState<HTMLElement | null>(null);
  const [draftMileRadius, setDraftMileRadius] = useState(mileRadius);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const hasInitializedRef = useRef(false);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const isTypingRef = useRef(false);

  // Initialize from store on mount only
  useEffect(() => {
    if (!hasInitializedRef.current) {
      if (eventPageStore.filters.address) {
        setAddress(eventPageStore.filters.address);
      }
      if (
        eventPageStore.filters.categories &&
        eventPageStore.filters.categories.length > 0
      ) {
        setSelectedCategories(eventPageStore.filters.categories);
      }
      hasInitializedRef.current = true;
    }
  }, []);

  // Flatten categories with subcategories for display
  const flattenedCategories = useMemo(() => {
    const flattened: Array<{ category: Category; isSubcategory: boolean }> = [];

    eventPageStore.categories.forEach((cat) => {
      if (cat.subcategories && cat.subcategories.length > 0) {
        // Add parent category
        flattened.push({ category: cat, isSubcategory: false });
        // Add subcategories
        cat.subcategories.forEach((subcat) => {
          flattened.push({
            category: {
              ...subcat,
              parentCategory: cat,
              parentCategoryId: cat.id,
            },
            isSubcategory: true,
          });
        });
      } else if (cat.parentCategoryId) {
        // This is a subcategory at root level, find its parent
        const parentCat = eventPageStore.categories.find(
          (c) => c.id === cat.parentCategoryId
        );
        if (parentCat) {
          flattened.push({
            category: {
              ...cat,
              parentCategory: parentCat,
            },
            isSubcategory: true,
          });
        } else {
          flattened.push({ category: cat, isSubcategory: false });
        }
      } else {
        // Standalone category (no parent, no subcategories)
        flattened.push({ category: cat, isSubcategory: false });
      }
    });

    return flattened;
  }, [eventPageStore.categories]);

  const handleSearch = useCallback((isLocationChange: boolean = false) => {
    const dateRange: DateRange | undefined =
      startDate || endDate
        ? {
            start: startDate ? startDate.clone().startOf("day") : undefined,
            end: endDate ? endDate.clone().endOf("day") : undefined,
          }
        : undefined;
    const ageRange: NumberRange = selectedAge
      ? AGE_RANGES.find((r) => r.label === selectedAge) ||
        eventPageStore.filters.ageRange
      : eventPageStore.filters.ageRange;

    // Filter out parent category IDs (only send leaf categories)
    const findCategory = (
      cats: Category[],
      id: string
    ): Category | undefined => {
      for (const cat of cats) {
        if (cat.id === id) return cat;
        if (cat.subcategories) {
          const result = findCategory(cat.subcategories, id);
          if (result) return result;
        }
      }
      return undefined;
    };

    const childrenOnlyCategories = selectedCategories.filter((id) => {
      console.log("id", id);
      const cat = findCategory(eventPageStore.categories, id);
      console.log("cat", cat);
      return !(cat && cat.subcategories && cat.subcategories.length > 0);
    });

    const filters = {
      address: address,
      mileRadius: mileRadius,
      ageRange: {
        min: ageRange.min,
        max: ageRange.max,
      },
      dateRange: dateRange,
      categories:
        childrenOnlyCategories.length > 0 ? childrenOnlyCategories : undefined,
    };

    // Only an actual address/location change should clear mapBounds and
    // re-fit/recenter the map. Category, age, date-range, and radius
    // changes still trigger a search (the results list updates) but must
    // leave the map exactly where the user left it — otherwise the map
    // falls back to EventMap's DEFAULT_CENTER (center of the contiguous
    // USA) whenever there's no address/geolocation to center on.
    eventPageStore.setFilters(
      isLocationChange ? "location" : "category",
      filters,
      accountStore.userLocation,
      true,
      isLocationChange
    );
  }, [
    address,
    mileRadius,
    selectedCategories,
    selectedAge,
    startDate,
    endDate,
    eventPageStore,
    accountStore.userLocation,
  ]);

  const handleClear = () => {
    setAddress("");
    setMileRadius(EventSearchDefaults.MileRadius);
    prevFilters.current.mileRadius = EventSearchDefaults.MileRadius;
    setSelectedCategories([]);
    setSelectedAge("");
    setStartDate(null);
    setEndDate(null);
    setDraftStart(null);
    setDraftEnd(null);

    eventPageStore.setFilters(
      "location",
      {
        address: "",
        mileRadius: EventSearchDefaults.MileRadius,
        ageRange: eventPageStore.filters.ageRange,
        dateRange: undefined,
        categories: undefined,
      },
      accountStore.userLocation,
      true,
      true
    );

    // Maintain focus in address input after clearing
    setTimeout(() => {
      if (addressInputRef.current) {
        addressInputRef.current.focus();
      }
    }, 100);
  };

  // --- Date Range popover handlers ---
  const openDateMenu = (e: React.MouseEvent<HTMLElement>) => {
    // Sync draft to committed values when opening
    setDraftStart(startDate);
    setDraftEnd(endDate);
    setDateMenuEl(e.currentTarget);
  };

  const closeDateMenu = () => setDateMenuEl(null);

  const applyDateRange = () => {
    setStartDate(draftStart);
    setEndDate(draftEnd);
    closeDateMenu();
  };

  // --- Radius popover handlers ---
  const openRadiusMenu = (e: React.MouseEvent<HTMLElement>) => {
    setDraftMileRadius(mileRadius);
    setRadiusMenuEl(e.currentTarget);
  };

  const closeRadiusMenu = () => setRadiusMenuEl(null);

  const applyRadius = () => {
    setMileRadius(draftMileRadius);
    closeRadiusMenu();
  };

  const dateRangeLabel =
    startDate || endDate
      ? `${startDate ? startDate.format("MMM D") : "…"} – ${
          endDate ? endDate.format("MMM D") : "…"
        }`
      : "Date Range";

  // Ref to track previous values to detect actual changes (skip initial mount)
  const prevFilters = useRef({
    address: "",
    mileRadius: eventPageStore.filters.mileRadius || EventSearchDefaults.MileRadius,
    selectedCategories: [] as string[],
    selectedAge: "",
    startDate: "",
    endDate: "",
  });
  const addressDebounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Debounced address search effect
  useEffect(() => {
    // Skip if address hasn't actually changed from previous value
    if (address === prevFilters.current.address) {
      return;
    }

    // Update previous value
    prevFilters.current.address = address;

    // Mark that user is typing
    isTypingRef.current = true;

    // Clear existing timer
    if (addressDebounceTimer.current) {
      clearTimeout(addressDebounceTimer.current);
    }

    // Debounce address changes by 500ms
    addressDebounceTimer.current = setTimeout(() => {
      isTypingRef.current = false;
      handleSearch(true);
      // Ensure focus remains after search completes
      setTimeout(() => {
        if (
          addressInputRef.current &&
          document.activeElement !== addressInputRef.current
        ) {
          addressInputRef.current.focus();
        }
      }, 100);
    }, 500);

    return () => {
      if (addressDebounceTimer.current) {
        clearTimeout(addressDebounceTimer.current);
      }
    };
  }, [address, handleSearch]);

  // Maintain focus in address input while typing and after results update
  useEffect(() => {
    // If user is typing, address changed, or input was focused, maintain focus
    if (
      isTypingRef.current ||
      addressInputRef.current === document.activeElement ||
      address !== prevFilters.current.address
    ) {
      // Small delay to ensure focus isn't lost during re-renders
      const timeoutId = setTimeout(() => {
        if (
          addressInputRef.current &&
          document.activeElement !== addressInputRef.current
        ) {
          addressInputRef.current.focus();
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [
    eventPageStore.events,
    eventPageStore.paginationStore.totalCount,
    address,
  ]);

  // Ensure focus is maintained when address becomes empty (e.g. user backspaces
  // it out while typing). Must NOT fire on a fresh mount/remount, where address
  // is also "" by default — that stole focus (and, on mobile, dragged the page
  // scroll back to this field) every time this component remounted, e.g. when
  // closing the event-detail view on mobile re-renders the filters bar.
  useEffect(() => {
    const hadFocus =
      isTypingRef.current || addressInputRef.current === document.activeElement;
    if (address === "" && addressInputRef.current && hadFocus) {
      // If address is cleared, maintain focus in the input
      const timeoutId = setTimeout(() => {
        if (
          addressInputRef.current &&
          document.activeElement !== addressInputRef.current
        ) {
          addressInputRef.current.focus();
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [address]);

  // Auto-search when categories, age, or date range change.
  // Radius alone (no address) does not trigger a search — matches mobile,
  // where the address field is required before the filter sheet can apply
  // (event_filter_sheet.dart validator). Radius is only meaningful once a
  // location search is active; it's picked up by the address-debounce effect
  // above the moment an address is typed.
  useEffect(() => {
    // Check if any filter has actually changed
    const startKey = startDate ? startDate.toISOString() : "";
    const endKey = endDate ? endDate.toISOString() : "";
    const radiusChanged =
      mileRadius !== prevFilters.current.mileRadius && Boolean(address);
    const hasChanged =
      JSON.stringify(selectedCategories) !==
        JSON.stringify(prevFilters.current.selectedCategories) ||
      selectedAge !== prevFilters.current.selectedAge ||
      startKey !== prevFilters.current.startDate ||
      endKey !== prevFilters.current.endDate ||
      radiusChanged;

    // Keep the ref in sync even when we skip searching, so a radius change
    // made before an address is entered doesn't retroactively look "changed"
    // once the address-debounce effect runs its own search.
    prevFilters.current.mileRadius = mileRadius;

    if (!hasChanged) {
      return;
    }

    // Update previous values
    prevFilters.current.selectedCategories = selectedCategories;
    prevFilters.current.selectedAge = selectedAge;
    prevFilters.current.startDate = startKey;
    prevFilters.current.endDate = endKey;

    // A radius change (with an address already set) still re-fits the map
    // to the new search area; category/age/date-range changes alone must
    // not move the map (see handleSearch).
    handleSearch(radiusChanged);
  }, [selectedCategories, selectedAge, startDate, endDate, mileRadius, address, handleSearch]);

  return (
    <Box sx={{ mb: 3, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
      {/* First Row - Date, Category, Age */}
      <Box
        sx={{
          display: "flex",
          gap: { xs: 1.5, md: 2 },
          mb: 2,
          flexDirection: { xs: "column", md: "row" },
          width: "100%",
          overflow: "hidden",
        }}
      >
        {/* Core date selector — single "Date Range" field opens a popover with
            Start/End DatePickers + Apply (restores previous origin/main behavior).
            Client requires this; preset Quick Select dropdown is POSTPONED, commented below. */}
        <FormControl
          sx={{
            flex: { xs: "none", md: 1 },
            width: { xs: "100%", md: "auto" },
            minWidth: { xs: 0, md: 120 },
          }}
        >
          <Box
            onClick={openDateMenu}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: "56px",
              padding: "0 14px",
              borderRadius: "25px",
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              cursor: "pointer",
              transition: "border-color 0.2s",
              "&:hover": { borderColor: "#FF6B35" },
              ...(Boolean(dateMenuEl) && { borderColor: "#FF6B35" }),
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <CalendarToday
                sx={{ fontSize: { xs: "1rem", md: "1.25rem" }, color: "#666" }}
              />
              <Box
                sx={{
                  fontSize: { xs: "0.875rem", md: "1rem" },
                  color: "#000",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {dateRangeLabel}
              </Box>
            </Box>
            <ExpandMore
              sx={{
                fontSize: "1.25rem",
                color: "#666",
                transition: "transform 0.2s",
                transform: dateMenuEl ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </Box>
        </FormControl>

        <Menu
          anchorEl={dateMenuEl}
          open={Boolean(dateMenuEl)}
          onClose={closeDateMenu}
          disableAutoFocus
          disableEnforceFocus
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          // Sit below the DatePicker calendar popup (which defaults to
          // theme.zIndex.modal) so the calendar renders in front, not behind.
          sx={{ zIndex: (theme) => theme.zIndex.modal - 1 }}
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "nowrap",
                  alignItems: "flex-start",
                }}
              >
                <DatePicker
                  label="Start Date"
                  value={draftStart}
                  minDate={moment()}
                  onChange={(newValue) => {
                    setDraftStart(newValue);
                    // keep end >= start
                    if (newValue && draftEnd && draftEnd.isBefore(newValue)) {
                      setDraftEnd(null);
                    }
                  }}
                  slotProps={{
                    textField: { sx: { ...dateFieldSx, width: 180 } },
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={draftEnd}
                  minDate={draftStart || moment()}
                  disabled={!draftStart}
                  onChange={(newValue) => setDraftEnd(newValue)}
                  slotProps={{
                    textField: {
                      sx: { ...dateFieldSx, width: 180 },
                      helperText: !draftStart
                        ? "Select a start date first"
                        : undefined,
                    },
                  }}
                />
              </Box>
            </LocalizationProvider>
            <Button
              variant="contained"
              onClick={applyDateRange}
              sx={{
                alignSelf: "flex-end",
                borderRadius: "25px",
                textTransform: "none",
              }}
            >
              Apply
            </Button>
          </Box>
        </Menu>

        {/* POSTPONED — preset "Quick Select" date range dropdown (re-enable when approved).
        <FormControl
          sx={{
            flex: { xs: "none", md: 1 },
            width: { xs: "100%", md: "auto" },
            minWidth: { xs: 0, md: 120 },
          }}
        >
          <Select
            value={selectedDateRange}
            onChange={(e) => setSelectedDateRange(e.target.value)}
            displayEmpty
            startAdornment={
              <InputAdornment position="start">
                <CalendarToday
                  sx={{
                    fontSize: { xs: "1rem", md: "1.25rem" },
                    color: "#666",
                  }}
                />
              </InputAdornment>
            }
            sx={{
              borderRadius: "25px",
              backgroundColor: "white",
              width: "100%",
              "& .MuiSelect-select": {
                paddingLeft: { xs: "8px", md: "14px" },
                fontSize: { xs: "0.875rem", md: "1rem" },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e0e0e0",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FF6B35",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FF6B35",
              },
            }}
          >
            <MenuItem value="">Date Range</MenuItem>
            {DATE_RANGE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        */}

        <FormControl
          sx={{
            flex: { xs: "none", md: 1 },
            width: { xs: "100%", md: "auto" },
            minWidth: { xs: 0, md: 120 },
          }}
        >
          <Box
            onClick={() => setIsCategoryDialogOpen(true)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: "56px",
              padding: "0 14px",
              borderRadius: "25px",
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              cursor: "pointer",
              transition: "border-color 0.2s",
              "&:hover": {
                borderColor: "#FF6B35",
              },
              ...(isCategoryDialogOpen && {
                borderColor: "#FF6B35",
              }),
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FilterList
                sx={{
                  fontSize: { xs: "1rem", md: "1.25rem" },
                  color: "#666",
                }}
              />
              <Box
                sx={{
                  fontSize: { xs: "0.875rem", md: "1rem" },
                  color: selectedCategories.length > 0 ? "#000" : "#000",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: { md: "180px", xs: "100%" },
                }}
              >
                {selectedCategories.length === 0
                  ? "Category"
                  : selectedCategories.length === 1
                    ? flattenedCategories.find(
                        (item) => item.category.id === selectedCategories[0]
                      )?.category.name || "Category"
                    : `${selectedCategories.length} Categories`}
              </Box>
            </Box>
            <ExpandMore
              sx={{
                fontSize: "1.25rem",
                color: "#666",
                transition: "transform 0.2s",
                transform: isCategoryDialogOpen
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
              }}
            />
          </Box>
        </FormControl>

        <CategorySelectDialog
          open={isCategoryDialogOpen}
          onClose={() => setIsCategoryDialogOpen(false)}
          onSave={(categories) => setSelectedCategories(categories)}
          categories={eventPageStore.categories}
          selectedCategories={selectedCategories}
          title="Filter by Category"
        />

        <FormControl
          sx={{
            flex: { xs: "none", md: 1 },
            width: { xs: "100%", md: "auto" },
            minWidth: { xs: 0, md: 120 },
          }}
        >
          <Select
            value={selectedAge}
            onChange={(e) => setSelectedAge(e.target.value)}
            displayEmpty
            MenuProps={{ PaperProps: { sx: { borderRadius: 2 } } }}
            startAdornment={
              <InputAdornment position="start">
                <SocialDistance
                  sx={{
                    fontSize: { xs: "1rem", md: "1.25rem" },
                    color: "#666",
                  }}
                />
              </InputAdornment>
            }
            sx={{
              borderRadius: "25px",
              backgroundColor: "white",
              width: "100%",
              "& .MuiSelect-select": {
                paddingLeft: { xs: "8px", md: "14px" },
                fontSize: { xs: "0.875rem", md: "1rem" },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e0e0e0",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FF6B35",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FF6B35",
              },
            }}
          >
            <MenuItem value="">Age</MenuItem>
            {AGE_RANGES.map((range) => (
              <MenuItem key={range.label} value={range.label}>
                {range.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Second Row - Address + Radius */}
      <Box
        sx={{
          display: "flex",
          gap: { xs: 1.5, md: 2 },
          width: "100%",
          overflow: "hidden",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <TextField
          fullWidth
          placeholder="Address, City, or Zip"
          value={address}
          onChange={(e) => {
            setAddress(e.target.value);
            // Ensure focus stays in the input when typing
            if (addressInputRef.current) {
              setTimeout(() => {
                addressInputRef.current?.focus();
              }, 0);
            }
          }}
          inputRef={addressInputRef}
          onFocus={(e) => {
            // Ensure the input maintains focus
            e.target.focus();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LocationOn
                  sx={{
                    fontSize: { xs: "1rem", md: "1.25rem" },
                    color: "#666",
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: { xs: "none", md: 1 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "25px",
              backgroundColor: "white",
              fontSize: { xs: "0.875rem", md: "1rem" },
              "& fieldset": {
                borderColor: "#e0e0e0",
              },
              "&:hover fieldset": {
                borderColor: "#FF6B35",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#FF6B35",
              },
            },
          }}
        />

        {/* Radius field — dropdown-style box (matches Age select), opens popover
            slider mirroring the native app's event_filter_sheet.dart _mileRadius slider */}
        <FormControl
          sx={{
            flex: { xs: "none", md: "0 0 160px" },
            width: { xs: "100%", md: "160px" },
          }}
        >
          <Box
            onClick={openRadiusMenu}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: "56px",
              padding: "0 14px",
              borderRadius: "25px",
              backgroundColor: "white",
              border: "1px solid #e0e0e0",
              cursor: "pointer",
              transition: "border-color 0.2s",
              "&:hover": { borderColor: "#FF6B35" },
              ...(Boolean(radiusMenuEl) && { borderColor: "#FF6B35" }),
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <SocialDistance
                sx={{ fontSize: { xs: "1rem", md: "1.25rem" }, color: "#666" }}
              />
              <Box
                sx={{
                  fontSize: { xs: "0.875rem", md: "1rem" },
                  color: "#000",
                  whiteSpace: "nowrap",
                }}
              >
                {mileRadius} miles
              </Box>
            </Box>
            <ExpandMore
              sx={{
                fontSize: "1.25rem",
                color: "#666",
                transition: "transform 0.2s",
                transform: radiusMenuEl ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </Box>
        </FormControl>

        <Menu
          anchorEl={radiusMenuEl}
          open={Boolean(radiusMenuEl)}
          onClose={closeRadiusMenu}
          disableAutoFocus
          disableEnforceFocus
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          sx={{ zIndex: (theme) => theme.zIndex.modal - 1 }}
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <Box sx={{ p: 2, width: 260, display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ fontSize: "0.875rem", color: "#666" }}>
              Search radius: {draftMileRadius} miles
            </Box>
            <Slider
              color="primary"
              value={draftMileRadius}
              min={5}
              max={250}
              step={5}
              onChange={(_, value) => setDraftMileRadius(value as number)}
              sx={{ mx: 1, width: "auto" }}
            />
            <Button
              variant="contained"
              onClick={applyRadius}
              sx={{
                alignSelf: "flex-end",
                borderRadius: "25px",
                textTransform: "none",
              }}
            >
              Apply
            </Button>
          </Box>
        </Menu>
      </Box>

      {/* Results Count and Clear Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
          gap: 1,
          width: "100%",
          overflow: "hidden",
          flexWrap: { xs: "wrap", sm: "nowrap" },
        }}
      >
        <Box
          sx={{
            fontSize: { xs: "0.875rem", md: "1rem" },
            fontWeight: 600,
            color: "#1a1a1a",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {eventPageStore.paginationStore.totalCount ||
            eventPageStore.events?.length ||
            0}{" "}
          Results
        </Box>
        <Button
          variant="text"
          onClick={handleClear}
          sx={{
            color: "#666",
            borderRadius: "25px",
            border: "1px solid #e0e0e0",
            textTransform: "none",
            fontSize: { xs: "0.75rem", md: "0.875rem" },
            padding: { xs: "6px 12px", md: "8px 16px" },
            flexShrink: 0,
            "&:hover": {
              backgroundColor: "#e0e0e0",
              color: "#FF6B35",
            },
          }}
        >
          Clear
        </Button>
      </Box>
    </Box>
  );
});
